// @ts-check
import React, { useCallback, useState } from 'react';

import _ from 'lodash';
import { Tabs as PatternFlyTabs, Tab, TabTitleText, TabTitleIcon } from '@patternfly/react-core';
import { useTranslation } from '../../localization/useTranslation';
import PropTypes from 'prop-types';
import styles from './Tabs.module.css';
/**
 * @typedef {object} TabType
 * @prop {string} label
 * @prop {React.FunctionComponent<any>} component
 * @prop {JSX.Element} [icon]
 * @prop {object} [props]
 */
const TabPropType = PropTypes.shape({
  label: PropTypes.string.isRequired,
  props: PropTypes.object,
  component: PropTypes.oneOfType([PropTypes.node, PropTypes.func, PropTypes.object]).isRequired
});

/**
 * @typedef {object} TabsProps
 * @prop {TabType[]} tabs array of tabs in the format { label: string, component: JSX.Element }
 * @prop {number} [initialTab=0]
 * @prop {function (number): void} [onSelectCallback]
 * @prop {string} [tabClassName='']
*/

/**
 * Show a standardized tabs list
 * @param {TabsProps} props
 * @returns {JSX.Element}
 */
const Tabs = ({ tabs, initialTab = 0, onSelectCallback, tabClassName = '' }) => {
  const T = useTranslation();
  const [activeTab, setActiveTab] = useState(initialTab);
  const _setActiveTab = useCallback((unusedEvent, tabIndex) => {
    setActiveTab(tabIndex);

    if (onSelectCallback) {
      onSelectCallback(tabIndex);
    }
  }, [onSelectCallback]);

  return (
    <PatternFlyTabs mountOnEnter unmountOnExit activeKey={activeTab} onSelect={_setActiveTab} isBox >
      { _.map(tabs, (tab, tabIndex) => (
        <Tab
          className={tabClassName}
          eventKey={tabIndex}
          key={tabIndex}
          title={<div className={styles.tabsTitle}>
            { tab?.icon && <TabTitleIcon className={styles.tabsTitleIcon}>{ tab?.icon }</TabTitleIcon> }
            <TabTitleText>{ T.localize(tab.label) }</TabTitleText>
          </div>}>
          { React.createElement(tab.component, tab.props) }
        </Tab>
      )) }
    </PatternFlyTabs>
  );
};

Tabs.propTypes = {
  tabs: PropTypes.arrayOf(TabPropType),
  initialTab: PropTypes.number,
  onSelectCallback: PropTypes.func,
  tabClassName: PropTypes.string
};

export default Tabs;
